import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import { useGravityToast } from '@/components/effects/GravityToast';
import api from '@/utils/api';
import ScrollReveal from '@/components/effects/ScrollReveal';
import GoldenSpiral from '@/components/effects/GoldenSpiral';
import { BookOpen, Download, MapPin, Headphones, ChevronLeft, ChevronRight, Eye, Mail, FileText, ArrowRight, Check, Sparkles, BookText, Globe, Bell } from 'lucide-react';
import { useGeoCurrency } from '@/hooks/useGeoCurrency';
import FlipBook from '@/components/effects/FlipBook';
import UpgradeAccountBanner from '@/components/UpgradeAccountBanner';

const faqItems = [
  {
    q_en: 'What is "Center of Gravity" about?',
    q_ar: 'عن ماذا يتحدث كتاب "مركز الجاذبية"؟',
    a_en: 'Center of Gravity explores how to build an influential presence that attracts attention naturally — instead of chasing it. It covers the 5 Laws of Attention, overcoming hidden fears, and becoming the gravitational center in any space.',
    a_ar: 'يستكشف مركز الجاذبية كيفية بناء حضور مؤثر يجذب الانتباه بشكل طبيعي — بدلاً من مطاردته. يغطي القوانين الخمسة للانتباه، والتغلب على المخاوف الخفية، وأن تصبح مركز الجاذبية في أي مكان.',
  },
  {
    q_en: 'Who is this book for?',
    q_ar: 'لمن هذا الكتاب؟',
    a_en: 'For anyone who feels they deserve more visibility, influence, and recognition — entrepreneurs, creators, leaders, and anyone seeking to understand the psychology of attention and build a magnetic presence.',
    a_ar: 'لكل من يشعر أنه يستحق مزيداً من الظهور والتأثير والاعتراف — رواد الأعمال، والمبدعون، والقادة، وكل من يسعى لفهم سيكولوجية الانتباه وبناء حضور مغناطيسي.',
  },
  {
    q_en: 'Is the book available in English?',
    q_ar: 'هل الكتاب متاح باللغة الإنجليزية؟',
    a_en: 'The book is currently written in Arabic. An English edition is being planned. Subscribe to the newsletter to be the first to know when it launches.',
    a_ar: 'الكتاب مكتوب حالياً باللغة العربية. يجري التخطيط لنسخة إنجليزية. اشترك في النشرة الإخبارية لتكون أول من يعرف عند الإطلاق.',
  },
  {
    q_en: 'What formats are available?',
    q_ar: 'ما هي الصيغ المتاحة؟',
    a_en: 'The book is available as a physical paperback (180 pages) and as a digital PDF. The physical copy ships worldwide.',
    a_ar: 'الكتاب متاح ككتاب ورقي (١٨٠ صفحة) وكملف PDF رقمي. النسخة الورقية تُشحن لجميع أنحاء العالم.',
  },
  {
    q_en: 'How many pages does it have?',
    q_ar: 'كم عدد صفحات الكتاب؟',
    a_en: 'The book is 180 pages total, with 160 pages of core content across 12 chapters — from the Laws of Attention to building a magnetic identity.',
    a_ar: 'الكتاب ١٨٠ صفحة إجمالاً، منها ١٦٠ صفحة من المحتوى الأساسي عبر ١٢ فصلاً — من قوانين الانتباه إلى بناء هوية مغناطيسية.',
  },
  {
    q_en: 'Can I get a free preview?',
    q_ar: 'هل يمكنني الحصول على معاينة مجانية؟',
    a_en: 'Yes! Subscribe to our Reader Magnet above with your email and get a free PDF of Chapter 1 — "The Center of Gravity Guide" — delivered instantly.',
    a_ar: 'نعم! اشترك في النشرة الإخبارية أعلاه ببريدك الإلكتروني واحصل على PDF مجاني من الفصل الأول — "دليل مركز الجاذبية" — يُرسل فوراً.',
  },
  {
    q_en: 'What payment methods do you accept?',
    q_ar: 'ما هي طرق الدفع المتاحة؟',
    a_en: 'We accept PayPal and credit/debit cards through our secure checkout. Prices are shown in USD by default, or ILS if you are browsing from Israel.',
    a_ar: 'نقبل الدفع عبر PayPal وبطاقات الائتمان/الخصم من خلال صفحة دفع آمنة. الأسعار تُعرض بالدولار الأمريكي افتراضياً، أو بالشيكل إذا كنت تتصفح من إسرائيل.',
  },
];

export default function Book() {
  const { t, localizedField, lang } = useLang();
  const toast = useGravityToast();
  const navigate = useNavigate();
  const [physical, setPhysical] = useState(null);
  const [digital, setDigital] = useState(null);
  const [audiobook, setAudiobook] = useState(null);
  const [zones, setZones] = useState([]);
  const [locations, setLocations] = useState([]);
  const [previewPages, setPreviewPages] = useState([]);
  const [activeSection, setActiveSection] = useState('intro');
  const [previewIdx, setPreviewIdx] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [magnetEmail, setMagnetEmail] = useState('');
  const [magnetName, setMagnetName] = useState('');
  const [magnetLoading, setMagnetLoading] = useState(false);
  const [magnetToken, setMagnetToken] = useState(null);
  const [enEmail, setEnEmail] = useState('');
  const [enName, setEnName] = useState('');
  const [enLoading, setEnLoading] = useState(false);
  const [enDone, setEnDone] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);

  // Mobile sticky buy bar — appears once the buyer scrolls past the hero
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setShowStickyBar(window.scrollY > 600);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const { currency } = useGeoCurrency();
  const isILS = currency === 'ILS';
  const location = useLocation();

  // Support deep links like /book#free-chapter from the landing page
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [location.hash]);

  useEffect(() => {
    api.get('/products/center-of-gravity').then(r => setPhysical(r.data)).catch(() => {});
    api.get('/products/center-of-gravity-digital').then(r => setDigital(r.data)).catch(() => {});
    api.get('/products/center-of-gravity-audiobook').then(r => setAudiobook(r.data)).catch(() => {});
    api.get('/shipping-zones').then(r => setZones(r.data)).catch(() => {});
    api.get('/book-availability').then(r => setLocations(r.data)).catch(() => {});
    api.get('/book-preview').then(r => setPreviewPages(r.data || [])).catch(() => {});
  }, []);

  const physicalAvailable = physical && physical.is_published !== false;
  const digitalAvailable = digital && digital.is_published !== false;
  const audiobookAvailable = audiobook && audiobook.is_published !== false;

  const sections = [
    { key: 'intro', label: t('book.previewIntro') },
    { key: 'toc', label: t('book.previewToc') },
    { key: 'chapter1', label: t('book.previewChapter') },
  ];

  const filteredPages = previewPages.filter(p => p.section === activeSection);

  const handleBuy = (type) => {
    const product = type === 'physical' ? physical : type === 'audiobook' ? audiobook : digital;
    if (product?.id) {
      navigate(`/checkout?type=product&id=${product.id}&currency=${currency}`);
    } else {
      toast.info('Product not available yet.');
    }
  };

  const handleMagnetSubmit = async (e) => {
    e.preventDefault();
    if (!magnetEmail.trim()) return;
    setMagnetLoading(true);
    try {
      const { data } = await api.post('/reader-magnet', { email: magnetEmail, name: magnetName, email_lang: lang });
      setMagnetToken(data.download_token);
      if (data.already_subscribed) {
        toast.success(lang === 'ar' ? 'تم إعادة إرسال الفصل إلى بريدك' : 'Chapter re-sent to your email');
      } else {
        toast.success(t('readerMagnet.success'));
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setMagnetLoading(false);
    }
  };

  const magnetDownloadUrl = magnetToken
    ? `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/download-chapter?token=${magnetToken}`
    : null;

  return (
    <div data-testid="book-page" className="min-h-screen">
      <UpgradeAccountBanner />
      {/* Hero */}
      <section className="py-20 md:py-32 px-4 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <GoldenSpiral size={500} className="animate-spiral-rotate" opacity={0.04} />
        </div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 md:gap-20 items-center relative z-10">
          <ScrollReveal>
            <div className="flex justify-center">
              <FlipBook className="w-72 md:w-96 aspect-[2/3]" />
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="space-y-6">
              <h1 className="text-4xl sm:text-5xl font-display text-heading" data-testid="book-page-title">
                {localizedField(physical?.title) || t('book.title')}
              </h1>
              <p className="text-xl font-accent italic text-sacred-gold">{t('book.subtitle')}</p>
              <p className="text-warm-brown font-body leading-relaxed">{localizedField(physical?.description)}</p>
              <div className="flex flex-wrap gap-4 text-sm text-text-muted/70 font-body">
                <span className="flex items-center gap-1.5"><BookText size={14} className="text-sacred-gold/60" /> {lang === 'ar' ? '١٦٠ من أصل ١٨٠ صفحة' : '160 of 180 pages'}</span>
                {physical?.metadata && (
                  <>
                    <span>By {physical.metadata.author}</span>
                    <span>{physical.metadata.format}</span>
                  </>
                )}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Purchase Options */}
      <section className="py-16 px-4" data-testid="purchase-options">
        <div className="max-w-4xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Physical */}
          <ScrollReveal>
            <div className={`glass-card rounded-2xl p-6 space-y-4 h-full ${!physicalAvailable ? 'opacity-60' : ''}`} data-testid="card-physical">
              <div className="w-12 h-12 rounded-full bg-sacred-gold/10 flex items-center justify-center">
                <BookOpen size={24} className="text-sacred-gold" />
              </div>
              <h3 className="text-xl font-display text-heading">{t('book.physical')}</h3>
              {physicalAvailable ? (
                <>
                  <p className="text-3xl font-display text-deep-brown">{isILS && physical?.price_ils ? `\u20AA${physical.price_ils}` : `$${physical?.price_usd || '22.99'}`}</p>
                  {physical?.price_ils && !isILS && <p className="text-sm text-text-muted/70">&#8362;{physical.price_ils} ILS</p>}
                  {physical?.price_usd && isILS && <p className="text-sm text-text-muted/70">${physical.price_usd} USD</p>}
                  <p className="text-text-muted text-sm font-body">+ {t('book.shipping')}</p>
                  <button onClick={() => handleBuy('physical')} className="w-full gold-button px-4 py-3 rounded-full font-body text-sm" data-testid="buy-physical">
                    {t('book.buyNow')}
                  </button>
                </>
              ) : (
                <>
                  <span className="inline-block px-3 py-1 rounded-full bg-sacred-gold/10 text-sacred-gold text-xs font-body">{t('book.comingSoon')}</span>
                  <p className="text-text-muted/50 text-sm font-body">{lang === 'ar' ? 'غير متوفر للبيع الآن' : 'Not available right now'}</p>
                </>
              )}
            </div>
          </ScrollReveal>

          {/* Digital */}
          <ScrollReveal delay={0.1}>
            <div className={`glass-card rounded-2xl p-6 space-y-4 h-full ${!digitalAvailable ? 'opacity-60' : ''}`} data-testid="card-digital">
              <div className="w-12 h-12 rounded-full bg-sacred-gold/10 flex items-center justify-center">
                <Download size={24} className="text-sacred-gold" />
              </div>
              <h3 className="text-xl font-display text-heading">{t('book.digital')}</h3>
              {digitalAvailable ? (
                <>
                  <p className="text-3xl font-display text-deep-brown">{isILS && digital?.price_ils ? `\u20AA${digital.price_ils}` : `$${digital?.price_usd || '17.99'}`}</p>
                  {digital?.price_ils && !isILS && <p className="text-sm text-text-muted/70">&#8362;{digital.price_ils} ILS</p>}
                  {digital?.price_usd && isILS && <p className="text-sm text-text-muted/70">${digital.price_usd} USD</p>}
                  <p className="text-text-muted text-sm font-body">PDF — {lang === 'ar' ? 'تنزيل فوري' : 'Instant download'}</p>
                  <button onClick={() => handleBuy('digital')} className="w-full gold-button px-4 py-3 rounded-full font-body text-sm" data-testid="buy-digital">
                    {t('book.buyDigital')}
                  </button>
                </>
              ) : (
                <>
                  <span className="inline-block px-3 py-1 rounded-full bg-sacred-gold/10 text-sacred-gold text-xs font-body">{t('book.comingSoon')}</span>
                  <p className="text-text-muted/50 text-sm font-body">{lang === 'ar' ? 'النسخة الرقمية قريبًا' : 'Digital edition coming soon'}</p>
                </>
              )}
            </div>
          </ScrollReveal>

          {/* Audiobook */}
          <ScrollReveal delay={0.2}>
            <div className={`glass-card rounded-2xl p-6 space-y-4 h-full ${!audiobookAvailable ? 'opacity-60' : ''}`} data-testid="card-audiobook">
              <div className="w-12 h-12 rounded-full bg-sacred-gold/10 flex items-center justify-center">
                <Headphones size={24} className="text-sacred-gold" />
              </div>
              <h3 className="text-xl font-display text-heading">{t('book.audiobook')}</h3>
              {audiobookAvailable ? (
                <>
                  {(audiobook?.price_usd || audiobook?.price_ils) && (
                    <p className="text-3xl font-display text-deep-brown">{isILS && audiobook?.price_ils ? `\u20AA${audiobook.price_ils}` : `$${audiobook.price_usd}`}</p>
                  )}
                  <p className="text-text-muted text-sm font-body">{lang === 'ar' ? 'بصوت طارق أبو فياض' : 'Narrated by Tariq AbuFiad'}</p>
                  <button onClick={() => handleBuy('audiobook')} className="w-full gold-button px-4 py-3 rounded-full font-body text-sm" data-testid="buy-audiobook">
                    {t('book.buyNow')}
                  </button>
                </>
              ) : (
                <>
                  <span className="inline-block px-3 py-1 rounded-full bg-sacred-gold/10 text-sacred-gold text-xs font-body">{t('book.comingSoon')}</span>
                  <p className="text-text-muted/50 text-sm font-body">{lang === 'ar' ? 'بصوت طارق أبو فياض' : 'Narrated by Tariq AbuFiad'}</p>
                </>
              )}
            </div>
          </ScrollReveal>
          {/* Trust signals */}
          <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 pt-4" data-testid="trust-signals">
            <span className="flex items-center gap-2 text-text-muted/70 text-xs font-body">
              <ShieldCheck size={14} className="text-sacred-gold/70" />
              {lang === 'ar' ? 'دفع آمن عبر PayPal والبطاقات' : 'Secure checkout — PayPal & cards'}
            </span>
            <span className="flex items-center gap-2 text-text-muted/70 text-xs font-body">
              <Globe size={14} className="text-sacred-gold/70" />
              {lang === 'ar' ? 'شحن لجميع أنحاء العالم' : 'Ships worldwide'}
            </span>
            <span className="flex items-center gap-2 text-text-muted/70 text-xs font-body">
              <Download size={14} className="text-sacred-gold/70" />
              {lang === 'ar' ? 'النسخة الرقمية: تنزيل فوري' : 'Digital: instant download'}
            </span>
          </div>
        </div>
      </section>

      {/* Reader Magnet — Free Chapter */}
      <section id="free-chapter" className="py-20 px-4 relative overflow-hidden scroll-mt-24" data-testid="reader-magnet-section">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-warm-black/5 to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto relative z-10">
          <ScrollReveal>
            <div className="relative rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a1611 0%, #2a2318 40%, #1a1611 100%)' }}>
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 opacity-10 pointer-events-none">
                <GoldenSpiral size={250} opacity={0.3} />
              </div>
              <div className="absolute bottom-0 left-0 w-40 h-40 rounded-full bg-sacred-gold/5 blur-3xl pointer-events-none" />

              <div className="grid md:grid-cols-5 gap-0">
                {/* Left: Visual */}
                <div className="md:col-span-2 p-8 md:p-10 flex flex-col items-center justify-center relative">
                  <div className="absolute top-4 start-4 md:top-6 md:start-6">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sacred-gold/20 text-sacred-gold text-xs font-body tracking-wider uppercase" data-testid="magnet-badge">
                      <Sparkles size={12} />
                      {t('readerMagnet.badge')}
                    </span>
                  </div>
                  <div className="w-44 md:w-52 aspect-[3/4] rounded-lg overflow-hidden shadow-2xl transform -rotate-3 hover:rotate-0 transition-transform duration-500" style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.5), 0 0 40px rgba(200,165,92,0.1)' }}>
                    <div className="w-full h-full bg-gradient-to-br from-[#2a2318] to-[#1a1611] flex flex-col items-center justify-center p-6 border border-sacred-gold/20">
                      <div className="w-12 h-0.5 bg-sacred-gold/40 mb-4" />
                      <p className="text-sacred-gold/40 text-[10px] font-body uppercase tracking-[0.3em] mb-3">Chapter One</p>
                      <h4 className="text-lg font-display text-cream text-center leading-tight mb-2">
                        {lang === 'ar' ? 'مركز الجاذبية' : 'Center of Gravity'}
                      </h4>
                      <div className="w-8 h-0.5 bg-sacred-gold/30 my-3" />
                      <p className="text-sacred-gold/60 text-[10px] font-body">Tariq AbuFiad</p>
                      <div className="mt-4 flex items-center gap-1.5 text-sacred-gold/50">
                        <FileText size={14} />
                        <span className="text-[10px] font-body">PDF</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Form */}
                <div className="md:col-span-3 p-8 md:p-10 md:ps-4 flex flex-col justify-center">
                  <h2 className="text-2xl md:text-3xl font-display text-cream mb-3" data-testid="magnet-title">
                    {t('readerMagnet.title')}
                  </h2>
                  <p className="text-cream/60 font-body text-sm md:text-base leading-relaxed mb-8 max-w-md">
                    {t('readerMagnet.subtitle')}
                  </p>

                  {!magnetToken ? (
                    <form onSubmit={handleMagnetSubmit} className="space-y-3 max-w-md" data-testid="magnet-form">
                      <input
                        type="text"
                        value={magnetName}
                        onChange={e => setMagnetName(e.target.value)}
                        placeholder={t('readerMagnet.namePlaceholder')}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-sacred-gold/20 text-cream placeholder:text-cream/30 font-body text-sm focus:outline-none focus:border-sacred-gold/50 transition-colors"
                        data-testid="magnet-name-input"
                      />
                      <input
                        type="email"
                        value={magnetEmail}
                        onChange={e => setMagnetEmail(e.target.value)}
                        placeholder={t('readerMagnet.emailPlaceholder')}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-sacred-gold/20 text-cream placeholder:text-cream/30 font-body text-sm focus:outline-none focus:border-sacred-gold/50 transition-colors"
                        data-testid="magnet-email-input"
                      />
                      <button
                        type="submit"
                        disabled={magnetLoading}
                        className="w-full group flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-body text-sm font-medium transition-all duration-300 disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #c8a55c 0%, #a88a3e 100%)', color: '#1a1611' }}
                        data-testid="magnet-submit-btn"
                      >
                        {magnetLoading ? (
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-[#1a1611]/30 border-t-[#1a1611] rounded-full animate-spin" />
                            {t('readerMagnet.downloading')}
                          </span>
                        ) : (
                          <>
                            <Mail size={16} />
                            {t('readerMagnet.cta')}
                            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                          </>
                        )}
                      </button>
                      <p className="text-cream/30 text-xs font-body text-center mt-2">
                        {lang === 'ar' ? 'لا بريد مزعج. وعد.' : 'No spam. Ever. Promise.'}
                      </p>
                    </form>
                  ) : (
                    <div className="space-y-4 max-w-md" data-testid="magnet-success">
                      <div className="flex items-center gap-3 text-sacred-gold">
                        <div className="w-10 h-10 rounded-full bg-sacred-gold/20 flex items-center justify-center">
                          <Check size={20} />
                        </div>
                        <p className="font-body text-sm text-cream">{t('readerMagnet.success')}</p>
                      </div>
                      <a
                        href={magnetDownloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-body text-sm font-medium transition-all duration-300 hover:scale-[1.02]"
                        style={{ background: 'linear-gradient(135deg, #c8a55c 0%, #a88a3e 100%)', color: '#1a1611' }}
                        data-testid="magnet-download-btn"
                      >
                        <Download size={16} />
                        {t('readerMagnet.downloadNow')}
                      </a>
                      <p className="text-cream/40 text-xs font-body">
                        {lang === 'ar' ? 'تم إرسال رابط التحميل أيضًا إلى بريدك الإلكتروني.' : 'A download link was also sent to your email.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Book Preview Reader */}
      {previewPages.length > 0 && (
        <section className="py-16 px-4" data-testid="book-preview-section">
          <div className="max-w-5xl mx-auto">
            <ScrollReveal>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl sm:text-3xl font-display text-heading" data-testid="preview-title">
                  <Eye size={24} className="inline me-3 text-sacred-gold" />
                  {t('book.preview')}
                </h2>
              </div>

              <div className="flex flex-wrap gap-2 mb-6" data-testid="preview-tabs">
                {sections.map(s => {
                  const hasPages = previewPages.some(p => p.section === s.key);
                  if (!hasPages) return null;
                  return (
                    <button
                      key={s.key}
                      onClick={() => { setActiveSection(s.key); setPreviewIdx(0); }}
                      data-testid={`preview-tab-${s.key}`}
                      className={`px-5 py-2 rounded-full font-body text-sm transition-all ${
                        activeSection === s.key
                          ? 'gold-button'
                          : 'glass-button text-text-muted'
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>

              {filteredPages.length > 0 && (
                <div className="glass-card rounded-2xl p-4 md:p-6" data-testid="preview-viewer">
                  <div className="relative flex items-center justify-center min-h-[400px] md:min-h-[600px]">
                    <img
                      src={filteredPages[previewIdx]?.url}
                      alt={filteredPages[previewIdx]?.label || `Page ${previewIdx + 1}`}
                      className="max-w-full max-h-[70vh] object-contain rounded-lg"
                      data-testid="preview-page-image"
                    />
                  </div>

                  {filteredPages.length > 1 && (
                    <div className="flex items-center justify-center gap-4 mt-4" data-testid="preview-nav">
                      <button
                        onClick={() => setPreviewIdx(Math.max(0, previewIdx - 1))}
                        disabled={previewIdx === 0}
                        className="p-2 rounded-full glass-button disabled:opacity-30"
                        data-testid="preview-prev"
                      >
                        {lang === 'ar' ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                      </button>
                      <span className="text-text-muted font-body text-sm" data-testid="preview-counter">
                        {previewIdx + 1} / {filteredPages.length}
                      </span>
                      <button
                        onClick={() => setPreviewIdx(Math.min(filteredPages.length - 1, previewIdx + 1))}
                        disabled={previewIdx === filteredPages.length - 1}
                        className="p-2 rounded-full glass-button disabled:opacity-30"
                        data-testid="preview-next"
                      >
                        {lang === 'ar' ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
                      </button>
                    </div>
                  )}

                  {filteredPages[previewIdx]?.label && (
                    <p className="text-center text-text-muted/70 text-sm font-body mt-2">{filteredPages[previewIdx].label}</p>
                  )}
                </div>
              )}
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Availability */}
      {locations.length > 0 && (
        <section className="py-16 px-4" data-testid="availability-section">
          <div className="max-w-4xl mx-auto">
            <ScrollReveal>
              <h2 className="text-2xl font-display text-heading mb-6">{t('book.availability')}</h2>
              <div className="space-y-4">
                {(() => {
                  // Group locations by city (fallback bucket: "Other")
                  const groups = {};
                  locations.forEach(loc => {
                    const cityKey = (loc.city || '').trim() || (lang === 'ar' ? 'أخرى' : 'Other');
                    if (!groups[cityKey]) groups[cityKey] = [];
                    groups[cityKey].push(loc);
                  });
                  const sortedCities = Object.keys(groups).sort((a, b) => a.localeCompare(b));
                  return sortedCities.map(city => (
                    <div key={city} className="glass-card rounded-2xl p-5" data-testid={`city-group-${city}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <MapPin size={18} className="text-sacred-gold" />
                        <h3 className="font-display text-lg text-heading">{city}</h3>
                        <span className="text-text-muted/50 text-xs font-body">
                          · {groups[city].length} {lang === 'ar'
                            ? (groups[city].length === 1 ? 'متجر' : 'متاجر')
                            : (groups[city].length === 1 ? 'store' : 'stores')}
                        </span>
                      </div>
                      <ul className="space-y-2 ps-7">
                        {groups[city].map(loc => (
                          <li key={loc.id} className="font-body text-sm" data-testid={`location-${loc.id}`}>
                            <span className="text-deep-brown font-medium">{localizedField(loc.location_name)}</span>
                            {loc.address && (
                              <span className="text-text-muted/70"> — {loc.address}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ));
                })()}
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* English Edition Notification */}
      <section className="py-16 px-4" data-testid="english-edition-section">
        <div className="max-w-3xl mx-auto">
          <ScrollReveal>
            <div className="relative rounded-2xl overflow-hidden border border-sacred-gold/15" style={{ background: 'linear-gradient(135deg, #1e1a14 0%, #2a2318 50%, #1e1a14 100%)' }}>
              <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-sacred-gold/5 blur-3xl pointer-events-none" />
              <div className="p-8 md:p-10 relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-sacred-gold/15 flex items-center justify-center">
                    <Globe size={20} className="text-sacred-gold" />
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sacred-gold/10 text-sacred-gold text-xs font-body tracking-wider uppercase">
                    {lang === 'ar' ? 'قريبًا' : 'Coming Soon'}
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-display text-cream mb-2" data-testid="english-edition-title">
                  {lang === 'ar' ? 'النسخة الإنجليزية قادمة' : 'English Edition Coming Soon'}
                </h2>
                <p className="text-cream/60 font-body text-sm leading-relaxed mb-6 max-w-lg">
                  {lang === 'ar'
                    ? 'كن أول من يعرف عند إصدار النسخة الإنجليزية من "مركز الجاذبية". سجّل بريدك الإلكتروني وسنُعلمك فور التوفر.'
                    : 'Be the first to know when the English edition of "Center of Gravity" is released. Drop your email and we\'ll notify you the moment it\'s available.'}
                </p>

                {!enDone ? (
                  <form
                    data-testid="english-notify-form"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (!enEmail.trim()) return;
                      setEnLoading(true);
                      try {
                        const { data } = await api.post('/english-notify', { email: enEmail, name: enName, email_lang: lang });
                        setEnDone(true);
                        toast.success(data.already_registered
                          ? (lang === 'ar' ? 'أنت مسجّل بالفعل!' : 'You\'re already on the list!')
                          : (lang === 'ar' ? 'تم التسجيل بنجاح!' : 'You\'re on the list!'));
                      } catch {
                        toast.error(lang === 'ar' ? 'حدث خطأ. حاول مجدداً.' : 'Something went wrong. Try again.');
                      } finally {
                        setEnLoading(false);
                      }
                    }}
                    className="flex flex-col sm:flex-row gap-3 max-w-lg"
                  >
                    <input
                      type="text"
                      value={enName}
                      onChange={e => setEnName(e.target.value)}
                      placeholder={lang === 'ar' ? 'الاسم (اختياري)' : 'Name (optional)'}
                      className="px-4 py-3 rounded-xl bg-white/5 border border-sacred-gold/20 text-cream placeholder:text-cream/30 font-body text-sm focus:outline-none focus:border-sacred-gold/50 flex-1 min-w-0"
                      data-testid="english-notify-name"
                    />
                    <input
                      type="email"
                      value={enEmail}
                      onChange={e => setEnEmail(e.target.value)}
                      placeholder={lang === 'ar' ? 'بريدك الإلكتروني' : 'Your email'}
                      required
                      className="px-4 py-3 rounded-xl bg-white/5 border border-sacred-gold/20 text-cream placeholder:text-cream/30 font-body text-sm focus:outline-none focus:border-sacred-gold/50 flex-1 min-w-0"
                      data-testid="english-notify-email"
                    />
                    <button
                      type="submit"
                      disabled={enLoading}
                      className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-body text-sm font-medium whitespace-nowrap disabled:opacity-50"
                      style={{ background: 'linear-gradient(135deg, #c8a55c 0%, #a88a3e 100%)', color: '#1a1611' }}
                      data-testid="english-notify-submit"
                    >
                      <Bell size={16} />
                      {enLoading ? '...' : (lang === 'ar' ? 'أعلمني' : 'Notify Me')}
                    </button>
                  </form>
                ) : (
                  <div className="flex items-center gap-3" data-testid="english-notify-success">
                    <div className="w-10 h-10 rounded-full bg-sacred-gold/20 flex items-center justify-center">
                      <Check size={20} className="text-sacred-gold" />
                    </div>
                    <p className="font-body text-sm text-cream">
                      {lang === 'ar' ? 'ممتاز! سنُعلمك فور توفر النسخة الإنجليزية.' : 'You\'re on the list! We\'ll notify you when it launches.'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Readers Also Asked */}
      <section className="py-16 px-4" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <ScrollReveal>
            <h2 className="text-2xl font-display text-heading mb-2 text-center">
              {lang === 'ar' ? 'أسئلة شائعة من القرّاء' : 'Readers Also Asked'}
            </h2>
            <p className="text-text-muted/60 text-sm font-body text-center mb-8">
              {lang === 'ar' ? 'كل ما تحتاج معرفته عن مركز الجاذبية' : 'Everything you need to know about Center of Gravity'}
            </p>
          </ScrollReveal>
          <div className="space-y-3">
            {faqItems.map((faq, i) => (
              <ScrollReveal key={i} delay={0.05 * (i + 1)}>
                <details className="glass-card rounded-xl group" data-testid={`faq-item-${i}`}>
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between gap-3 font-body text-sm font-semibold text-heading select-none list-none [&::-webkit-details-marker]:hidden">
                    <span>{lang === 'ar' ? faq.q_ar : faq.q_en}</span>
                    <ChevronRight size={16} className="text-sacred-gold/60 shrink-0 transition-transform duration-200 group-open:rotate-90" />
                  </summary>
                  <div className="px-5 pb-4 text-warm-brown text-sm font-body leading-relaxed border-t border-sacred-gold/8 pt-3">
                    {lang === 'ar' ? faq.a_ar : faq.a_en}
                  </div>
                </details>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sticky mobile buy bar */}
      {(physicalAvailable || digitalAvailable) && (
        <div
          className={`md:hidden fixed bottom-0 inset-x-0 z-40 transition-transform duration-300 ${showStickyBar ? 'translate-y-0' : 'translate-y-full'}`}
          data-testid="sticky-buy-bar"
        >
          <div className="mx-3 mb-3 rounded-2xl px-4 py-3 flex items-center justify-between gap-3 border border-sacred-gold/25 shadow-2xl" style={{ background: 'rgba(20, 17, 12, 0.92)', backdropFilter: 'blur(14px)' }}>
            <div className="min-w-0">
              <p className="text-cream font-display text-sm truncate">{localizedField(physical?.title) || t('book.title')}</p>
              <p className="text-sacred-gold font-body text-xs">
                {physicalAvailable
                  ? (isILS && physical?.price_ils ? `₪${physical.price_ils}` : `$${physical?.price_usd}`)
                  : (isILS && digital?.price_ils ? `₪${digital.price_ils}` : `$${digital?.price_usd}`)}
              </p>
            </div>
            <button
              onClick={() => handleBuy(physicalAvailable ? 'physical' : 'digital')}
              className="gold-button px-5 py-2.5 rounded-full font-body text-sm shrink-0"
              data-testid="sticky-buy-btn"
            >
              {t('book.buyNow')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
